// @ts-check
// Execuções do bot (aba Execuções das estatísticas; contrato de bots, seções 19.1 e 22.3): situação, motivo do fim,
// bloco atual, o contato e o link para a conversa quando o membro vê a conversa, e os passos de cada execução (só o
// caminho e os nomes dos campos gravados, nunca o texto). Filtros por situação e por bloco; "Mostrar mais" pagina.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { getBotRun, listBotRuns } from '../../data/bots.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { formatDateTime } from '../../lib/format.js';
import { indexBotTree, walkBotTree } from '../../lib/bot-tree.js';
import { BOT_RUN_STATUSES } from '../../../../shared/bots/bot-reasons.js';
import { botBranchLabel, botReasonText, describeBotNode } from './bot-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/bots.js').BotRunSummary} BotRunSummary
 * @typedef {import('../../data/bots.js').BotRunStep} BotRunStep
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('../../../../shared/bots/bot-summary.js').BotSummaryLookups} BotSummaryLookups
 */

const PAGE_SIZE = 25;

/** @param {{ botId: string, definition: BotDefinition, lookups: BotSummaryLookups }} props */
export function BotRunsList({ botId, definition, lookups }) {
  const [status, setStatus] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [runs, setRuns] = useState(/** @type {BotRunSummary[] | null} */ (null));
  const [cursor, setCursor] = useState(/** @type {string | null} */ (null));
  const [hasMore, setHasMore] = useState(false);
  const [openRunId, setOpenRunId] = useState(/** @type {string | null} */ (null));
  const [steps, setSteps] = useState(/** @type {Record<string, BotRunStep[]>} */ ({}));
  const index = indexBotTree(definition);
  /** @param {string | null} nodeKey */
  const nodeTitle = (nodeKey) => {
    const node = nodeKey ? index.get(nodeKey)?.node : null;
    return node ? describeBotNode(node, lookups).title : '-';
  };
  /** @param {boolean} append */
  const load = async (append) => {
    try {
      const result = await listBotRuns(botId, { status: status || null, nodeId: nodeId || null, limit: PAGE_SIZE, startingAfter: append ? cursor : null });
      setRuns((current) => (append && current ? [...current, ...result.runs] : result.runs));
      setCursor(result.pageInfo.endCursor);
      setHasMore(result.pageInfo.hasNextPage);
    } catch (error) {
      showErrorSnackbar(error);
      setRuns([]);
    }
  };
  useEffect(() => {
    setRuns(null);
    load(false);
  }, [botId, status, nodeId]);
  /** @param {string} runId */
  const toggle = async (runId) => {
    if (openRunId === runId) {
      setOpenRunId(null);
      return;
    }
    setOpenRunId(runId);
    if (!steps[runId]) {
      try {
        const result = await getBotRun(runId);
        setSteps((current) => ({ ...current, [runId]: result.steps }));
      } catch (error) {
        showErrorSnackbar(error);
      }
    }
  };
  /** @type {Array<{ id: string, title: string }>} */
  const nodeChoices = [];
  walkBotTree(definition.nodes, (node) => nodeChoices.push({ id: node.id, title: describeBotNode(node, lookups).title }));
  return html`<div class="bot-runs" data-testid="bot-runs-list">
    <div class="bot-runs-filters">
      <select class="text-input" value=${status} aria-label=${t('bots.runs.filterStatus')} onChange=${(/** @type {Event} */ event) => setStatus(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
        <option value="">${t('bots.runs.anyStatus')}</option>
        ${BOT_RUN_STATUSES.map((value) => html`<option key=${value} value=${value}>${t(`bots.runs.statuses.${value}`)}</option>`)}
      </select>
      <select class="text-input" value=${nodeId} aria-label=${t('bots.runs.filterNode')} onChange=${(/** @type {Event} */ event) => setNodeId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
        <option value="">${t('bots.runs.anyNode')}</option>
        ${nodeChoices.map((choice, position) => html`<option key=${choice.id} value=${choice.id}>${`${position + 1}. ${choice.title}`}</option>`)}
      </select>
    </div>
    ${runs === null
      ? html`<${Skeleton} height=${120} />`
      : runs.length === 0
        ? html`<${EmptyState} icon="IconHistory" title=${t('bots.runs.empty')} />`
        : html`<div class="flow-stats-table-wrapper"><table class="flow-stats-table bot-runs-table">
            <thead><tr>
              <th scope="col">${t('bots.runs.columns.contact')}</th><th scope="col">${t('bots.runs.columns.status')}</th><th scope="col">${t('bots.runs.columns.reason')}</th>
              <th scope="col">${t('bots.runs.columns.node')}</th><th scope="col">${t('bots.runs.columns.startedAt')}</th><th scope="col">${t('bots.runs.columns.steps')}</th>
            </tr></thead>
            <tbody>${runs.map((run) => html`<tr key=${run.id} data-testid="bot-run-row">
                <th scope="row">
                  ${run.threadId && run.contactName
                    ? html`<a class="flow-summary-link" href=${`/conversations/${encodeURIComponent(run.threadId)}`}>${run.contactName}</a>`
                    : html`<span class="bot-runs-hidden">${t('bots.runs.hiddenContact')}</span>`}
                </th>
                <td><span class="bot-run-status" data-status=${run.status}>${t(`bots.runs.statuses.${run.status}`)}</span></td>
                <td>${run.endReason ? botReasonText('END', run.endReason) : '-'}</td>
                <td>${run.currentNodeId ? nodeTitle(run.currentNodeId) : '-'}</td>
                <td>${formatDateTime(run.startedAt)}</td>
                <td>
                  <button type="button" class="flow-link-button" aria-expanded=${openRunId === run.id ? 'true' : 'false'} onClick=${() => toggle(run.id)}>
                    <${Icon} name=${openRunId === run.id ? 'IconChevronUp' : 'IconChevronDown'} size=${14} />${t('bots.runs.stepCount', { count: run.stepCount })}
                  </button>
                  ${openRunId === run.id
                    ? steps[run.id]
                      ? html`<ol class="bot-test-steps bot-run-steps">${steps[run.id].map((step, position) => {
                          const node = index.get(step.nodeId)?.node ?? null;
                          return html`<li key=${position} data-status=${step.status}>
                            <span>${nodeTitle(step.nodeId)}</span>
                            <span class="bot-test-step-status">${t(`bots.stepStatuses.${step.status}`)}${step.branchKey && node ? ` · ${botBranchLabel(node, step.branchKey)}` : ''}</span>
                            ${step.reason ? html`<span class="bot-test-step-reason">${botReasonText('STEP', step.reason)}</span>` : null}
                            ${step.fieldsWritten?.length ? html`<span class="bot-test-step-reason">${t('bots.runs.fieldsWritten', { fields: step.fieldsWritten.join(', ') })}</span>` : null}
                          </li>`;
                        })}</ol>`
                      : html`<${Skeleton} height=${40} />`
                    : null}
                </td>
              </tr>`)}</tbody>
          </table></div>
          ${hasMore ? html`<${Button} title=${t('bots.runs.more')} variant="secondary" size="small" onClick=${() => load(true)} />` : null}`}
    <p class="flow-field-hint">${t('bots.runs.privacyHint')}</p>
  </div>`;
}
